/**
 * CONTEXT_MAP — runtime context registry.
 *
 * Keyed by the URL segment used in
 *   /programs/:programId/runtime/:context/:mode/:entityId?
 *
 * Each entry tells the universal RuntimeFormPage:
 *   • what the entity is called in UI copy
 *   • which PAGE_ROUTE_KEYS.* drives the instruction banner
 *   • which EntityFormResolution entityType to fetch the schema for
 *   • which search params carry parent ids (hcp → hospitalId, patient → hospitalId + hcpId)
 *   • whether the context supports edit mode
 *   • where to navigate after save
 *
 * Keep this map the single source of truth for per-context metadata.
 */

import { Building2, User, Users, FileText, PhoneCall, Pill, PackageCheck } from 'lucide-react';
import type { ComponentType } from 'react';

import { PAGE_ROUTE_KEYS } from '@/modules/shared/types/pageInstruction';
import { programRoutes } from '@/modules/shared/constants/routes';

export type RuntimeContextKey =
  | 'hospital'
  | 'hcp'
  | 'patient'
  | 'scp'
  | 'callLog'
  | 'pharmacy'
  | 'dispenseCase';

export type RuntimeMode = 'create' | 'edit';

export interface RuntimeContextConfig {
  /** Human label used in headers / copy */
  label: string;
  /** Pluralised label for list/nav references */
  labelPlural: string;
  /** Backend entity type for EntityFormResolution API. Null → not resolvable via that endpoint yet. */
  entityType: 'HOSPITAL' | 'HCP' | 'PATIENT' | 'SCP' | 'CALL_LOG' | 'PHARMACY' | 'DISPENSE_CASE' | null;
  /** Icon shown in the runtime header */
  Icon: ComponentType<{ className?: string }>;
  /** PAGE_ROUTE_KEYS used by PageInstructionBanner */
  pageInstructionKey: {
    create: string;
    edit: string;
  };
  /** Query-string keys carrying parent references into the form */
  parentParams: string[];
  /** Whether /runtime/:context/edit/:entityId is supported */
  supportsEdit: boolean;
  /**
   * Where to navigate after a successful submit.
   * Defaults to program workspace when unset.
   */
  postSubmitPath?: (programId: string) => string;
}

export const CONTEXT_MAP: Record<RuntimeContextKey, RuntimeContextConfig> = {
  hospital: {
    label: 'Hospital',
    labelPlural: 'Hospitals',
    entityType: 'HOSPITAL',
    Icon: Building2,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.HOSPITAL_CREATE,
      edit: PAGE_ROUTE_KEYS.HOSPITAL_EDIT,
    },
    parentParams: [],
    supportsEdit: true,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
  hcp: {
    label: 'HCP',
    labelPlural: 'HCPs',
    entityType: 'HCP',
    Icon: Users,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.HCP_CREATE,
      edit: PAGE_ROUTE_KEYS.HCP_EDIT,
    },
    parentParams: ['hospitalId'],
    supportsEdit: true,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
  patient: {
    label: 'Patient',
    labelPlural: 'Patients',
    entityType: 'PATIENT',
    Icon: User,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.PATIENT_CREATE,
      edit: PAGE_ROUTE_KEYS.PATIENT_EDIT,
    },
    parentParams: ['hospitalId', 'hcpId'],
    supportsEdit: true,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
  scp: {
    label: 'Assistance Case',
    labelPlural: 'Assistance Cases',
    entityType: 'SCP',
    Icon: FileText,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.ASSISTANCE_CASE_CREATE,
      edit: PAGE_ROUTE_KEYS.ASSISTANCE_CASE_EDIT,
    },
    parentParams: ['patientId', 'benefitPlanId'],
    supportsEdit: false,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
  callLog: {
    label: 'Call Log',
    labelPlural: 'Call Logs',
    entityType: 'CALL_LOG',
    Icon: PhoneCall,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.CALL_LOG_FORM,
      edit: PAGE_ROUTE_KEYS.CALL_LOG_FORM,
    },
    parentParams: ['patientId'],
    supportsEdit: false,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
  pharmacy: {
    label: 'Pharmacy',
    labelPlural: 'Pharmacies',
    entityType: 'PHARMACY',
    Icon: Pill,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.PHARMACY_CREATE,
      edit: PAGE_ROUTE_KEYS.PHARMACY_EDIT,
    },
    parentParams: ['hospitalId'],
    supportsEdit: true,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
  dispenseCase: {
    label: 'Dispense Case',
    labelPlural: 'Dispense Cases',
    entityType: 'DISPENSE_CASE',
    Icon: PackageCheck,
    pageInstructionKey: {
      create: PAGE_ROUTE_KEYS.DISPENSE_CASE_CREATE,
      edit: PAGE_ROUTE_KEYS.DISPENSE_CASE_EDIT,
    },
    parentParams: ['patientId', 'hospitalId', 'pharmacyId', 'caseId'],
    supportsEdit: false,
    postSubmitPath: (programId) => programRoutes.workspace(programId),
  },
};

export function isValidContext(value: string | undefined): value is RuntimeContextKey {
  return !!value && value in CONTEXT_MAP;
}

export function isValidMode(value: string | undefined): value is RuntimeMode {
  return value === 'create' || value === 'edit';
}
